import React, { useEffect, useState } from "react";
import { Client, ClientType } from "../../types/client.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { useNavigate, useParams } from "react-router-dom";
import CreateTransaction from "../../components/CreateTransaction/createTransaction.component";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Clients() {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const { type } = useParams();
  let typeName: string;
  switch (type) {
    case "purchase":
      typeName = "Покупка";
      break;
    case "sale":
      typeName = "Продажа";
      break;
    case "scrap":
      typeName = "Лом";
      break;
    default:
      typeName = "Неизвестный тип";
  }

  const { data: cashboxesList, loading: cashboxListLoading } =
    useGetRequest(`/cashbox/list`);
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currency, setCurrency] = useState<Currency | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [clientHasDebt, setClientHasDebt] = useState<boolean>(false);
  const [companyHasDebt, setCompanyHasDebt] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPrepaidModalOpen, setIsPrepaidModalOpen] = useState<boolean>(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] =
    useState<boolean>(false); 
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [form] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [prepaidForm] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  useEffect(() => {
    fetchClients();
  }, [
    page,
    pageSize,
    searchTerm,
    sortBy,
    sortOrder,
    clientHasDebt,
    companyHasDebt,
    currency,
    type,
  ]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const fetchClients = async () => {
    setLoading(true);

    try {
      const response = await api.get("/client", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          sortBy,
          order: sortOrder,
          clientHasDebt: clientHasDebt ? true : undefined,
          companyHasDebt: companyHasDebt ? true : undefined,
          currency,
          clientType: typeName,
        },
      });
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching clients", error);
      message.error("Ошибка при получение клиентов.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddClient = () => {
    form.resetFields();
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    form.setFieldsValue(client);
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleSubmitForm = async (values: Partial<Client>) => {
    setConfirmLoading(true);

    try {
      if (selectedClient) {
        await api.put(`/client/${selectedClient._id}`, values);
        message.success("Клиент успешно обновлен.");
      } else {
        await api.post("/client", values);
        message.success("Клиент успешно добавлен.");
      }
      fetchClients();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving client", error);
      message.error(error.response.data.error);
    }
  };

  const handleTransactionClick = (client: Client) => {
    setSelectedClient(client);
    setIsTransactionModalOpen(true);
    transactionForm.resetFields();
  };

  const handlePrepaidClick = (client: Client) => {
    setSelectedClient(client);
    setIsPrepaidModalOpen(true);
    prepaidForm.resetFields();
  };

  const handleExport = () => {
    downloadFile("/export/clients", "clients_data.xlsx", {
      clientType: typeName,
    });
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Долг клиента в USD",
      dataIndex: "totalClientDebt",
      key: "totalClientDebt",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Наш долг в USD",
      key: "totalCompanyDebt",
      dataIndex: "totalCompanyDebt",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Создан",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => {
        const date = new Date(createdAt);
        const formattedDate = date.toLocaleDateString("ru-RU");
        return <p>{formattedDate}</p>;
      },
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, client: Client) => {
        const menu = (
          <Menu>
            {authState.user?.role !== UserRole.SalesDept && (
              <>
                <Menu.Item onClick={() => handleTransactionClick(client)}>
                  {typeName === "Продажа" ? "Оплата от клиента" : "Оплатить"}
                </Menu.Item>
                <Menu.Item onClick={() => handlePrepaidClick(client)}>
                  Возврат предоплаты
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${client._id}/client/${client.name}/USD&UZS/transactions`
                    )
                  }
                >
                  Смотреть транзакции
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${client._id}/client/${client.clientType}/${client.name}/orders`
                    )
                  }
                >
                  Заказы
                </Menu.Item>
              </>
            )}

            <Menu.Item onClick={() => handleEditClient(client)}>
              Изменить
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleCreateTransacttion = async (values: {
    amount: number;
    cashboxId: string;
    exchangeRate: number;
    description: string;
    machineId: string;
  }) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/client/transaction/${selectedClient?._id}`, data);
      message.success("Транзакция успешно создана.");

      fetchClients();
      setIsTransactionModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating transaction", error);
      message.error(error.response.data.error);
    }
  };

  const handleReturnPrepaid = async (values: {
    amount: number;
    exchangeRate: number;
    cashboxId: string;
    description: string;
    currency: Currency;
  }) => {
    setConfirmLoading(true);
    try {
            const data = {
          ...values,
          exchangeRate: exchangeRate || values.exchangeRate,
      };
      await api.post(`/client/return-prepaid/${selectedClient?._id}`, data);
      message.success("Возврат преоплаты успешн создан.");

      fetchClients();
      setIsPrepaidModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating prepaid", error);
      message.error(error.response.data.error);
    }
  };
  return (
    <div style={{ padding: 24 }}>
      <Title>Клиенты {typeName}</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Сортировать по"
          defaultValue="createdAt"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="createdAt">Дата</Option>
          <Option value="name">Название</Option>
          <Option value="totalSoldAmount">Продано (сумма)</Option>
          <Option value="totalPurchasedAmount">Куплено (сумма)</Option>
          <Option value="totalReceivedAmount">Получено денег</Option>
          <Option value="totalPaidAmount">Заплачено денег</Option>
        </Select>
        <Select
          defaultValue="desc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={clientHasDebt}
          onChange={(e) => setClientHasDebt(e.target.checked)}
        >
          Клиент имеет долг
        </Checkbox>

        <Checkbox
          checked={companyHasDebt}
          onChange={(e) => setCompanyHasDebt(e.target.checked)}
        >
          У нас есть долг
        </Checkbox>
        <Button type="primary" onClick={handleAddClient}>
          Добавить
        </Button>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={clients}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title={selectedClient ? "Изменить клиента" : "Добавить клиента"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item name="clientType" initialValue={typeName}>
            <Select
              placeholder="Выберите тип клиента"
              value={typeName}
              disabled={selectedClient != null}
            >
              {Object.values(ClientType).map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Телефон"
            rules={[{ required: true, message: "Введите телефон" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="maxDiscount" label="Скидка лимит %">
            <InputNumber min={0} max={100} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={"Возврат предоплаты"}
        open={isPrepaidModalOpen}
        onCancel={() => setIsPrepaidModalOpen(false)}
        onOk={() => prepaidForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={prepaidForm} layout="vertical" onFinish={handleReturnPrepaid}>
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxListLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select
              placeholder="Выберите валюту"
              onChange={(value) => setCurrency(value)}
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      {selectedClient && (
        <CreateTransaction
          title="Создать транзакцию"
          open={isTransactionModalOpen}
          form={transactionForm}
          confirmLoading={confirmLoading}
          onCancel={() => setIsTransactionModalOpen(false)}
          onOk={() => transactionForm.submit()}
          onFinish={handleCreateTransacttion}
        />
      )}
    </div>
  );
}
